import { useRouter } from '@abyss/web/hooks/useRouter';
import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';
import { LoadingSpinner } from '@abyss/web/ui/LoadingSpinner';
import { useEffect } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../../../common/Constants';
import { LOADER_CENTER_STYLES } from '../../../../common/ConstantsStyles';
import { LagoonProvider } from '../../../../lagoon';
import { getLOBTargetInCookies } from '../../../../lagoon/lagoonUtil';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { getLanguage } from '../../../ProviderSearch/context/Internationalization/helpers';
import { Locale } from '../../../ProviderSearch/context/Internationalization/types';

const parsedLob = (lineOfBusiness: string, defaultValue: string) => {
  //for provider-directory uses only guest-plan-selection lob for lagoon, since header and footer is common with guest.
  switch (lineOfBusiness?.toLowerCase()) {
    case 'e&i':
    case 'eni':
    case 'm&r':
    case 'mnr':
    case 'c&s':
    case 'cns':
    case 'ifp':
    case 'obh':
    case 'umr':
    case 'guest':
    case 'guest-eni':
    case 'guest-mnr':
    case 'guest-cns':
    case 'guest-ifp':
    case 'guest-plan-selection':
      return 'guest-plan-selection';
    default:
      return defaultValue;
  }
};

export const Configuration = ({ children }: { children: React.ReactNode }) => {
  const lagoonEnvironment = config('LAGOON_ENV');
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const urlLocale = searchParams?.get('locale') as string;
  const urlLOB = searchParams?.get('lob') as string;
  const [lob, setLob] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.LOB,
    ''
  );

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { locale, setLocale } = chipStore;
  // Update lob in session storage when URL lob changes
  useEffect(() => {
    if (urlLOB) {
      setLob(urlLOB);
    }
  }, [urlLOB]);

  useEffect(() => {
    if (urlLocale) {
      setLocale(urlLocale);
    }
  }, [urlLocale]);

  const targetLob = lob || getLOBTargetInCookies();
  // Get supported languages from session storage (set by Lagoon) or fall back to potentially supported
  const supportedLanguagesFromStorage = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES
  );
  const supportedLanguages = supportedLanguagesFromStorage
    ? JSON.parse(supportedLanguagesFromStorage)
    : [Locale.enUS, Locale.esUS];

  const localeCode = getLanguage(locale, supportedLanguages)?.code || 'en';

  const loadingIcon = (
    <div style={LOADER_CENTER_STYLES}>
      <LoadingSpinner ariaLoadingLabel="Importing data" isLoading />
    </div>
  );
  return (
    <LagoonProvider
      app={'psx-web-guest'} // for now it will be always guest, since lob is defaulted to guest-plan-selection.
      env={lagoonEnvironment as string}
      loadingIcon={loadingIcon}
      lob={parsedLob(targetLob, 'guest-plan-selection')}
      locale={localeCode}
    >
      {children}
    </LagoonProvider>
  );
};
